import { useState } from "react";
import type { ReactNode } from "react";
import Uppy from "@uppy/core";
import { DashboardModal } from "@uppy/react";
import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import AwsS3 from "@uppy/aws-s3";
import type { UploadResult } from "@uppy/core";
import { Button } from "@/components/ui/button";

interface ObjectUploaderProps {
  maxNumberOfFiles?: number;
  maxFileSize?: number;
  onGetUploadParameters: () => Promise<{
    method: "PUT";
    url: string;
  }>;
  onComplete?: (
    result: UploadResult<Record<string, unknown>, Record<string, unknown>>
  ) => void;
  buttonClassName?: string;
  children: ReactNode;
}

/**
 * A file upload component that renders as a button and provides a modal interface for
 * file management.
 * 
 * Features:
 * - Renders as a customizable button that opens a file upload modal
 * - Provides a modal interface for:
 *   - File selection
 *   - File preview
 *   - Upload progress tracking
 *   - Upload status display
 * 
 * The component uses Uppy under the hood to handle all file upload functionality.
 * All file management features are automatically handled by the Uppy dashboard modal.
 */
export function ObjectUploader({
  maxNumberOfFiles = 1,
  maxFileSize = 10485760, // 10MB default
  onGetUploadParameters,
  onComplete,
  buttonClassName,
  children,
}: ObjectUploaderProps) {
  const [showModal, setShowModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  const [uppy] = useState(() => {
    const uppyInstance = new Uppy({
      restrictions: {
        maxNumberOfFiles,
        maxFileSize,
        allowedFileTypes: ['image/*'],
      },
      autoProceed: false,
    })
      .use(AwsS3, {
        shouldUseMultipart: false,
        getUploadParameters: onGetUploadParameters,
      })
      .on("upload", () => {
        setIsUploading(true);
      })
      .on("complete", (result) => {
        setIsUploading(false);
        if (result.successful && result.successful.length > 0) {
          setShowModal(false); // Close modal on successful upload
          // Clear files after successful upload
          uppyInstance.getFiles().forEach(file => uppyInstance.removeFile(file.id));
        }
        onComplete?.(result);
      })
      .on("error", (error) => {
        setIsUploading(false);
        console.error("Upload error:", error);
      })
      .on("upload-error", (file, error) => {
        setIsUploading(false);
        console.error("File upload error:", error);
      });
    
    return uppyInstance;
  });

  return (
    <div>
      <Button 
        onClick={() => setShowModal(true)} 
        className={buttonClassName}
        disabled={isUploading}
      >
        {isUploading ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            <span>Uploading...</span>
          </div>
        ) : (
          children
        )}
      </Button>

      <DashboardModal
        uppy={uppy}
        open={showModal}
        onRequestClose={() => !isUploading && setShowModal(false)}
        closeModalOnClickOutside={!isUploading}
        proudlyDisplayPoweredByUppy={false}
        locale={{
          strings: {
            done: 'Complete Upload',
            uploadComplete: 'Upload successful! Click "Complete Upload" to continue.',
            cancel: 'Cancel Upload',
            dropPasteFiles: 'Drop your graffiti photo here or click to browse',
            uploadXFiles: { 0: 'Upload %{smart_count} file', 1: 'Upload %{smart_count} files' }
          }
        }}
      />
    </div>
  );
}